import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation, Navigate } from "react-router-dom";
import { api } from "./api";
import { rememberReturnPath } from "./returnPath";

const AuthContext = createContext(null);
const AUTH_TOKEN_STORAGE_KEY = "austuition.access_token";

function persistAccessToken(token) {
  if (!token) return;
  try {
    window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
  } catch {
    // ignore storage issues
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionEpochRef = useRef(0);

  const establishSession = useCallback((data) => {
    sessionEpochRef.current += 1;
    persistAccessToken(data?.access_token);
    setUser(data || null);
    setLoading(false);
    return data;
  }, []);

  const refresh = useCallback(async () => {
    const epoch = sessionEpochRef.current;
    try {
      const { data } = await api.get("/auth/me");
      if (epoch !== sessionEpochRef.current) return data;
      setUser(data);
      return data;
    } catch {
      if (epoch !== sessionEpochRef.current) return null;
      try {
        window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
      } catch {
        // ignore storage issues
      }
      setUser(null);
      return null;
    } finally {
      if (epoch === sessionEpochRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", {
      email: String(email || "").trim().toLowerCase(),
      password,
    });
    return establishSession(data);
  }, [establishSession]);

  const register = useCallback(async (payload) => {
    const normalized = {
      ...payload,
      email: String(payload?.email || "").trim().toLowerCase(),
      name: String(payload?.name || "").trim(),
      phone: payload?.phone ? String(payload.phone).trim() : "",
      state: payload?.state ? String(payload.state).trim() : "",
      parent_account_link_code: payload?.parent_account_link_code
        ? String(payload.parent_account_link_code).trim().toUpperCase()
        : undefined,
    };
    const { data } = await api.post("/auth/register", normalized);
    return establishSession(data);
  }, [establishSession]);

  const logout = useCallback(async () => {
    sessionEpochRef.current += 1;
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    try {
      window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    } catch {
      // ignore storage issues
    }
    setUser(null);
    setLoading(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh, setUser }),
    [user, loading, login, register, logout, refresh],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-sm text-aus-onyx/50" data-testid="auth-loading">
        Loading...
      </div>
    );
  }
  const fromPath = `${location.pathname}${location.search || ""}`;
  if (!user) {
    rememberReturnPath(fromPath);
    return <Navigate to="/login" state={{ from: fromPath }} replace />;
  }
  if (roles && !(roles.includes(user.role) || roles.some((r) => (user.roles || []).includes(r)))) {
    return <Navigate to="/" replace />;
  }
  return children;
}
